/*
 * Icon chung của Sư Tử Con — vẽ từ sprite của Design System
 * (public/brand/sutucon/icons.svg, đồng bộ bởi design-system/sync-ds.mjs).
 * Đây là bộ icon DUY NHẤT của Simba (giống hệt Rafiki — bộ Design System dùng chung): không dùng lucide/heroicons.
 */
import type { SVGProps } from "react"
import { cn } from "@/lib/utils"

export const ICON_NAMES = [
  "home", "search", "user", "users", "settings", "bell", "mail", "phone", "map-pin",
  "calendar", "clock", "chart", "trending", "book", "book-open", "notebook",
  "graduation", "trophy", "award", "star", "heart", "check", "check-circle", "x",
  "plus", "more", "chevron-left", "chevron-right", "arrow-right", "play",
  "play-circle", "mic", "volume", "file", "folder", "download", "share", "lock",
  "globe", "chat", "send", "filter", "flame", "target", "lightbulb", "sparkles",
  "layers", "zap", "bot", "list",
] as const
export type IconName = (typeof ICON_NAMES)[number]

// Hướng xoay cho các icon dẫn xuất (chỉ có chevron-right trong bộ gốc).
const DERIVED: Record<string, [IconName, string]> = {
  "chevron-down": ["chevron-right", "rotate(90deg)"],
  "chevron-up": ["chevron-right", "rotate(-90deg)"],
  "arrow-left": ["arrow-right", "scaleX(-1)"],
}

export type AnyIconName = IconName | keyof typeof DERIVED

export function Icon({
  name,
  className,
  style,
  title,
  ...rest
}: SVGProps<SVGSVGElement> & { name: AnyIconName; title?: string }) {
  const [base, transform] = DERIVED[name] ?? [name, undefined]
  return (
    <svg
      viewBox="0 0 24 24"
      data-icon={name}
      className={cn("size-5 shrink-0", className)}
      style={transform ? { transform, ...style } : style}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      {...rest}
    >
      {title && <title>{title}</title>}
      <use href={`/brand/sutucon/icons.svg#${base}`} />
    </svg>
  )
}

/* shadcn sinh component với <IconPlaceholder lucide="…" />; ánh xạ sang icon brand. */
const FROM_LUCIDE: Record<string, AnyIconName> = {
  CheckIcon: "check",
  ChevronDownIcon: "chevron-down",
  ChevronUpIcon: "chevron-up",
  ChevronRightIcon: "chevron-right",
  ChevronLeftIcon: "chevron-left",
  MoreHorizontalIcon: "more",
  PanelLeftIcon: "list",
  XIcon: "x",
}

export function IconPlaceholder({
  lucide,
  className,
}: { lucide: string; className?: string; [lib: string]: unknown }) {
  return <Icon name={FROM_LUCIDE[lucide] ?? "more"} className={cn("size-4", className)} />
}

/** Chọn icon theo NGỮ NGHĨA của nhãn (tiếng Việt lẫn tiếng Anh). */
export function iconNameForText(value = ""): IconName {
  const text = value.toLocaleLowerCase("vi")
  if (text.includes("reflection") || text.includes("phản tư")) return "notebook"
  if (text.includes("product") || text.includes("kiến tạo")) return "layers"
  if (text.includes("key concept") || text.includes("ý tưởng")) return "lightbulb"
  if (text.includes("gia đình") || text.includes("bố") || text.includes("mẹ")) return "home"
  if (text.includes("lịch") || text.includes("nhịp")) return "calendar"
  if (text.includes("mentor") || text.includes("khóa học")) return "graduation"
  if (text.includes("góp ý") || text.includes("phản hồi")) return "chat"
  if (text.includes("bạn") || text.includes("cùng lớp")) return "users"
  if (text.includes("sẵn sàng") || text.includes("hoàn thành")) return "check-circle"
  if (text.includes("hồ sơ")) return "user"
  if (text.includes("simba") || text.includes("bắt đầu")) return "flame"
  if (text.includes("facts")) return "search"
  if (text.includes("feelings")) return "heart"
  if (text.includes("findings")) return "lightbulb"
  if (text.includes("future")) return "target"
  return "sparkles"
}

export function SemanticIcon({ label, className }: { label?: string; className?: string }) {
  return <Icon name={iconNameForText(label)} className={className} />
}
